import React from 'react';
import type { FC } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { SNIPPETS_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
	MODAL_OUTCOME,
} from '../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../hooks/useSnippetAnalytics.ts';
import { useSnippetsActions, useSnippetsState } from '../store/snippetsStore.ts';

import { FirstSnippetCreatedModal } from './FirstSnippetCreatedModal.tsx';
import { getSnippetBaseModalProps, SnippetBaseModal } from './SnippetBaseModal.tsx';
import { wrapAdfWithDocNode } from '../snippetUtils.ts';
import type { SnippetBaseModalFormValues } from './snippetModalFormUtils.ts';
import { getDefaultFormValues } from './snippetModalFormUtils.ts';
import { useSnippetModalClose } from './useSnippetModalClose.ts';

export const createSnippetModalMessages = defineMessages({
	titleCreate: {
		id: 'snippets.snippetEditorModal.titleCreate',
		defaultMessage: 'Create block template',
		description: 'Title for the create block template modal',
	},
	createButton: {
		id: 'snippets.snippetEditorModal.createButton',
		defaultMessage: 'Create',
		description: 'Create button text for create mode',
	},
	successFlagTitle: {
		id: 'snippets.snippetEditorModal.successFlagTitle',
		defaultMessage: 'Template "{snippetName}" created',
		description: 'Title for the success flag shown when template creation succeeds',
	},
	successFlagDescription: {
		id: 'snippets.snippetEditorModal.successFlagDescription',
		defaultMessage: 'You can now use it from the / menu',
		description: 'Description for the success flag shown when template creation succeeds',
	},
	errorFlagTitle: {
		id: 'snippets.snippetEditorModal.errorFlagTitle',
		defaultMessage: 'Could not create template',
		description: 'Title for the error flag shown when template creation fails',
	},
	errorFlagDescription: {
		id: 'snippets.snippetEditorModal.errorFlagDescription',
		defaultMessage: 'Please try again',
		description: 'Description for the error flag shown when template creation fails',
	},
});

export type CreateSnippetModalProps = {
	onClose: () => void;
	onCloseComplete: () => void;
	adf?: ADFEntity | ADFEntity[];
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
} & WithFlagsProps;

const FORM_KEY = 'create-snippet';

const CreateSnippetModalWithFlags: FC<CreateSnippetModalProps> = ({
	onClose,
	onCloseComplete,
	adf,
	contentId,
	spaceId,
	spaceKey,
	flags,
	editorAPI,
}) => {
	const intl = useIntl();
	const { isSnippetSharingForSiteEnabled = false } = useSnippetsState();
	const snippetsActions = useSnippetsActions();
	const { showModal } = useDialogs();
	const { fireFailedAnalytic } = useSnippetAnalytics(ANALYTICS_ORIGIN.CREATE_SNIPPET_MODAL);
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const handleClose = useSnippetModalClose(
		onClose,
		ANALYTICS_ORIGIN.CREATE_SNIPPET_MODAL,
		ANALYTICS_UI_SUBJECT_ID.CREATE_SNIPPET,
	);

	const {
		data: onboardingData,
		loading: onboardingLoading,
		error: onboardingError,
	} = useGetOnboardingState([SNIPPETS_ONBOARDING.HAS_SEEN_FIRST_SNIPPET_CREATED_MODAL], false);
	const { hasSeenFirstSnippetCreatedModal } = deserializeState(onboardingData);

	const defaultValues = getDefaultFormValues(isSnippetSharingForSiteEnabled);
	const initialAdf = wrapAdfWithDocNode(adf);
	const title = intl.formatMessage(createSnippetModalMessages.titleCreate);
	const submitLabel = intl.formatMessage(createSnippetModalMessages.createButton);

	const onSubmit = async (values: SnippetBaseModalFormValues, currentAdf: DocNode) => {
		const newSnippet = await snippetsActions.createSnippet(
			values.snippetName,
			currentAdf,
			values.snippetDescription,
			{
				flags,
				intl,
				editorAPI: editorAPI ?? undefined,
				isShared: values.isShared,
				onShareFailure: (snippetId) =>
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_SHARE, { snippetId }),
			},
		);
		if (newSnippet) {
			handleClose({
				outcome: MODAL_OUTCOME.CREATED,
				snippetId: newSnippet.snippetId,
			});

			const isOnboardingStateReady = !onboardingLoading && !onboardingError;
			const shouldShowFirstSnippetCreatedModal =
				!isEmbeddedConfluence && isOnboardingStateReady && !hasSeenFirstSnippetCreatedModal;
			if (shouldShowFirstSnippetCreatedModal) {
				showModal(FirstSnippetCreatedModal, {});
			}
		} else {
			fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_CREATE, {});
		}
	};

	return (
		<SnippetBaseModal
			{...getSnippetBaseModalProps({
				title,
				submitLabel,
				formKey: FORM_KEY,
				defaultValues,
				initialAdf,
				onSubmit,
				handleClose,
				fireFailedAnalytic,
				onCloseComplete,
				contentId,
				spaceId,
				spaceKey,
				flags,
				isSnippetSharingForSiteEnabled,
			})}
		/>
	);
};

export const CreateSnippetModal = withFlags(CreateSnippetModalWithFlags);
